import _ from 'lodash';

import { GetterTree } from 'vuex';
import { RootState } from '../types';
import { CustomerDictionaryState } from './types';
import {
  CustomerDictionary,
  DictionaryConditionMatrix,
  DictionaryItem,
  DictionaryType,
  DictionarySection,
  DictionaryRoom,
  Compliancequestion,
  QuestionWrapper,
  Maintenanceflag,
} from '@/models';

/**
 * Getters
 *
 * Vuex allows us to define "getters" in the store.
 * - You can think of them as computed properties for stores.
 * - Like computed properties, a getter's result is cached based on its dependencies,
 *   and will only re-evaluate when some of its dependencies have changed.
 */
export const getters: GetterTree<CustomerDictionaryState, RootState> = {

  /**
   * The updates which have been made since creating/loading/last-saving the current report
   * 
   * @param {CustomerDictionaryState} state string
   * @returns void
   */
  updates: (state: CustomerDictionaryState): string[] => state.updates,

  /**
   * The current CustomerDictionary, populated by GET {API_URL}/customerdictionary
   * 
   * @param {CustomerDictionaryState} Dictionary
   * @returns void
   */
  current: (state: CustomerDictionaryState): CustomerDictionary => state.current,

  /**
   * Is the Dictionary Empty?
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns populated customerdictionary state 
   */
  isEmpty: (state: CustomerDictionaryState): boolean => {
    return state.current.rooms.length === 0 && state.current.sections.length === 0;
  },

  /**
   * The updates which have been made since creating/loading/last-saving the current report
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasUnsavedChanges: (state: CustomerDictionaryState): boolean => state.updates.length > 0,

  /**
   * Has Active Section?
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasActiveSection: (state: CustomerDictionaryState): boolean => state.activeSection.slug.length > 0,

  /**
   * Has Active Type?
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasActiveType: (state: CustomerDictionaryState): boolean => state.activeType.slug.length > 0,

  /**
   * Has Active Items?
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasActiveItem: (state: CustomerDictionaryState): boolean => state.activeItem.slug.length > 0,

  /**
   * Has Active Room
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasActiveRoom: (state: CustomerDictionaryState): boolean => state.activeRoom.slug.length > 0,

  /**
   * Has Active Room Category?
   * 
   * @param {CustomerDictionaryState} state boolean
   * @returns void
   */
  hasActiveRoomCategory: (state: CustomerDictionaryState): boolean => state.activeRoomCategory > 0,

  /**
   * Get all Dictionary Sections
   * 
   * @param {CustomerDictionaryState} state
   * @returns Dictionary sections
   */
  allSections: (state: CustomerDictionaryState): DictionarySection[] => {
    return state.current.sections;
  },

  /**
   * Get all Dictionary Types
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary section types
   */
  allTypes: (state: CustomerDictionaryState, getters: any): DictionaryType[] => {
    return getters.allSections.flatMap((section: DictionarySection) => {
      if (_.isUndefined(section.types)) {
        return null;
      }
      return section.types;
    }).filter(Boolean);
  },

  /**
   * Get current Dictionary Types
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary section types
   */
  currentTypes: (state: CustomerDictionaryState, getters: any): DictionaryType[] => {
    return getters.allSections.flatMap((section: DictionarySection) => {
      if (_.isUndefined(section.types)) {
        return null;
      }
      if (state.activeSection.slug !== section.slug) {
        return null;
      }
      return section.types;
    }).filter(Boolean);
  },

  /**
   * Get all Dictionary Types, sorted alphabetically
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Sorted Dictionary types
   */
  allTypesSorted: (state: CustomerDictionaryState, getters: any): DictionaryType[] => {
    return _.orderBy(getters.allTypes, ['name'], ['asc']);
  },

  /**
   * Get current Dictionary Types, sorted alphabetically
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Sorted Dictionary types
   */
  currentTypesSorted: (state: CustomerDictionaryState, getters: any): DictionaryType[] => {
    return _.orderBy(getters.currentTypes, ['name'], ['asc']);
  },

  /**
   * Get all Dictionary Items
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary items
   */
  allItems: (state: CustomerDictionaryState, getters: any): DictionaryItem[] => {
    return getters.allTypes.flatMap((type: DictionaryType) => {
      if (_.isUndefined(type.items)) {
        return null;
      }
      return type.items;
    }).filter(Boolean);
  },

  /**
   * Get current Dictionary Items
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary items
   */
  currentItems: (state: CustomerDictionaryState, getters: any): DictionaryItem[] => {
    return getters.currentTypes.flatMap((type: DictionaryType) => {
      if (_.isUndefined(type.items)) {
        return null;
      }
      if (state.activeType.slug !== type.slug) {
        return null;
      }
      return type.items;
    }).filter(Boolean);
  },

  /**
   * Get all Dictionary Items, sorted alphabetically
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Sort all Dictionary types
   */
  allItemsSorted: (state: CustomerDictionaryState, getters: any): DictionaryItem[] => {
    return _.orderBy(getters.allItems, ['name'], ['asc']);
  },

  /**
   * Get current Dictionary Items, sorted alphabetically
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Sort current Dictionary types
   */
  currentItemsSorted: (state: CustomerDictionaryState, getters: any): DictionaryItem[] => {
    return _.orderBy(getters.currentItems, ['name'], ['asc']);
  },

  /**
   * Get Dictionary "Misc" Section
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary "Misc" Section
   */
  miscSection: (state: CustomerDictionaryState, getters: any): DictionarySection => {
    const section = _.find(getters.allSections, (section: DictionarySection) => section.name === 'Misc');
    if (_.isUndefined(section)) {
      return new DictionarySection;
    }
    return section;
  },

  /**
   * Get Dictionary "Misc" Type
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary "Misc" Type
   */
  miscType: (state: CustomerDictionaryState, getters: any): DictionaryType => {
    const type = _.find(getters.miscSection.types, (type: DictionaryType) => type.name === 'Misc');
    if (_.isUndefined(type)) {
      return new DictionaryType;
    }
    return type;
  },

  /**
   * Get Dictionary "Misc" Item
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Dictionary "Misc" Item
   */
  miscItem: (state: CustomerDictionaryState, getters: any): DictionaryItem => {
    const item = _.find(getters.miscType.items, (item: DictionaryItem) => item.name === 'Misc');
    if (_.isUndefined(item)) {
      return new DictionaryItem;
    }
    return item;
  },

  /**
   * Room Options
   * - for Multiselect, and the Templates <table>
   * 
   * @param {CustomerDictionaryState} state 
   * @returns Rooms sorted by priority
   */
  roomsByPriority: (state: CustomerDictionaryState): DictionaryRoom[] => {
    if (!state.current.rooms) return [];

    return _.sortBy(state.current.rooms, ['priority', 'name']);
  },

  /**
   * All Colours
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Get all colours
   */
  allColours: (state: CustomerDictionaryState, getters: any): string[] => {
    const colours: string[] = getters.allItems.flatMap((item: DictionaryItem) => {
      return _.get(item, 'colours', []);
    });

    // Order alphabetically
    return _.orderBy(Array.from(new Set(colours)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * Get "Misc" Item Colours
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Ordered "Misc" colours
   */
  miscItemColours: (state: CustomerDictionaryState, getters: any): string[] => {
    const colours: string[] = getters.miscItem.colours;

    // Order alphabetically
    return _.orderBy(Array.from(new Set(colours)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * All Conditions (Additional)
   * - for Multiselect
   * - "Conditions (Additional)" are also used to populate the Condition Matrix dropdowns
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns Get all conditions
   */
  allConditions: (state: CustomerDictionaryState, getters: any): string[] => {
    const conditions: string[] = getters.allItems.flatMap((item: DictionaryItem) => {
      return _.get(item.conditions, 'additional', []);
    });

    // Order alphabetically
    return _.orderBy(Array.from(new Set(conditions)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * "Misc" Item Conditions (Additional)
   * - for Multiselect
   * - "Conditions (Additional)" are also used to populate the Condition Matrix dropdowns
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns additional misc condition notes
   */
  miscItemConditions: (state: CustomerDictionaryState, getters: any): string[] => {
    // (these have been ordered alphabetically by the mutation)
    return getters.miscItem.conditions.additional;
  },

  /**
   * All Constituents
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns All Constituents
   */
  allConstituents: (state: CustomerDictionaryState, getters: any): string[] => {
    const constituents: string[] = getters.allItems.flatMap((item: DictionaryItem) => {
      return _.get(item, 'constituents', []);
    });

    // Order alphabetically
    return _.orderBy(Array.from(new Set(constituents)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * Get "Misc" Item Constituents
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns additional misc constituents
   */
  miscItemConstituents: (state: CustomerDictionaryState, getters: any): string[] => {
    const constituents: string[] = getters.miscItem.constituents;

    // Order alphabetically
    return _.orderBy(Array.from(new Set(constituents)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * All Makes
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns All Makes
   */
  allMakes: (state: CustomerDictionaryState, getters: any): string[] => {
    const makes: string[] = getters.allItems.flatMap((item: DictionaryItem) => {
      return _.get(item, 'makes', []);
    });

    // Order alphabetically
    return _.orderBy(Array.from(new Set(makes)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * Get "Misc" Item Makes
   * - for Multiselect
   * 
   * @param {CustomerDictionaryState} state 
   * @param getters 
   * @returns additional misc Item Makes
   */
  miscItemMakes: (state: CustomerDictionaryState, getters: any): string[] => {
    const makes: string[] = getters.miscItem.makes;

    // Order alphabetically
    return _.orderBy(Array.from(new Set(makes)), (text) => text.toLowerCase(), ['asc']);
  },

  /**
   * Get Acitve Condition Matrix
   * 
   * @param {CustomerDictionaryState} state 
   * @returns void
   */
  activeConditionMatrix: (state: CustomerDictionaryState): DictionaryConditionMatrix[] => state.activeItem.conditions.matrix,

  /**
   * Get the list of rooms
   * 
   * @param {CustomerDictionaryState} state 
   * @returns array of Maintenanceflag type
   */
  rooms: (state: CustomerDictionaryState): DictionaryRoom[] => state.current.rooms,

  /**
   * Get selected room
   * @param {CustomerDictionaryState} state 
   * @returns a Compliancequestion type
   */
  selectedroom: (state: CustomerDictionaryState): DictionaryRoom => state.selected_room,

  /**
   * Get the list of maintenance flags
   * 
   * @param {CustomerDictionaryState} state 
   * @returns array of Maintenanceflag type
   */
  maintenanceflags: (state: CustomerDictionaryState): Maintenanceflag[] => state.current.maintenance_flags,

  /**
   * Get selected maintenance flag
   * @param {CustomerDictionaryState} state 
   * @returns a Compliancequestion type
   */
  selectedmaintenanceflag: (state: CustomerDictionaryState): Maintenanceflag => state.selected_maintenanceflag,

  /**
   * Get the list of compliance questions
   * 
   * @param {CustomerDictionaryState} state 
   * @returns array of Compliancequestion type
   */
  compliancelist: (state: CustomerDictionaryState): Compliancequestion[] => state.current.compliance_list,

  /**
   * Get selected question
   * @param {CustomerDictionaryState} state 
   * @returns a Compliancequestion type
   */
  selectedquestion: (state: CustomerDictionaryState): Compliancequestion => state.selected_question,

  /**
   * Get current selected question wrapper
   * @param {CustomerDictionaryState} state 
   * @returns a QuestionWrapper
   */
  questionwrapper: (state: CustomerDictionaryState): QuestionWrapper => state.questionwrapper,

  /**
   * Get TNC
   * @param {CustomerDictionaryState} state 
   * @returns a string
   */
  tnc: (state: CustomerDictionaryState): string | undefined => state.current.report_configuration.tnc,
};
